import { Button, tokens } from "@blurb/codex-react";
import { createStyles, SelectItem, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { useAllCustomers } from "../customer-products/CustomerProductFilter";
import {
  BetweenSearchInput,
  getDateRangeFilter,
  getInStatusFilter,
  isBetweenSearch,
  SearchInput,
  toSearchInputArray,
} from "../../configuration/filters/searchInput";
import { MultiselectInput, MultiselectValue } from "../../components/Multiselect";
import { paymentStatusRender, publicOrderStatusRender } from "../../utils/searchField.util";
import { useAuthUser } from "../../hooks/useAuthUser";
import { DatePickerInput } from "../../components/DatePicker";

export const path = "filter";
const NO_FILTER_VALUE = "All";
const filterFormId = "orders-filter-form";

const useFilterStyles = createStyles({
  root: {
    padding: `0 ${tokens.spacing[6]}`,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[6],
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: tokens.spacing[6],
    gap: tokens.spacing[2],
  },
});

type OrdersFilterProps = {
  isOpened: boolean;
  filters: SearchInput<GraphQL.OrderSearchField>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.OrderSearchField>[]) => void;
};
export function OrdersFilter({ isOpened, filters, onClose, onApply }: OrdersFilterProps) {
  const { classes } = useFilterStyles();
  const user = useAuthUser();
  const organizations = useAllCustomers(user.is.SUPER_ADMIN);

  const outletContext: InnerProps = {
    initialFilters: filters,
    organizations,
    onApply,
    onClose,
  };

  return (
    <RpaDrawer title="Filters" opened={isOpened} onClose={onClose}>
      <Outlet context={outletContext} />
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" fullWidth intent="primary" type="submit" form={filterFormId}>
            Apply
          </Button>
          <Button variant="text" intent="primary" type="reset" form={filterFormId}>
            Clear All
          </Button>
        </div>
      </RpaDrawer.Footer>
    </RpaDrawer>
  );
}

function getInitialStatuses<TStatuses>(
  field: GraphQL.OrderSearchField,
  filters: SearchInput<GraphQL.OrderSearchField>[]
): TStatuses[] {
  return (filters.find((value) => value.field === field)?.values as TStatuses[]) ?? [];
}

function getInitialDateRange(
  field: GraphQL.OrderSearchField,
  filters: SearchInput<GraphQL.OrderSearchField>[]
): [Date | null, Date | null] {
  const range = filters.find(
    (value): value is BetweenSearchInput<typeof field> =>
      value.field === field && isBetweenSearch<GraphQL.OrderSearchField>(value)
  );
  return range ? [new Date(range.firstValue), new Date(range.secondValue)] : [null, null];
}

type InnerProps = {
  initialFilters: SearchInput<GraphQL.OrderSearchField>[];
  organizations?: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.OrderSearchField>[]) => void;
};
export function OrdersFilterInner() {
  const { classes } = useFilterStyles();

  const { initialFilters, onClose, onApply, organizations } = useOutletContext<InnerProps>();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer:
        initialFilters.find((value) => value?.field === GraphQL.OrderSearchField.CustomerPublicId)?.firstValue ??
        NO_FILTER_VALUE,
    },
  });
  const [orgSearch, setOrgSearch] = useState<string>("");
  const [orderStatusFilter, setOrderStatusFilter] = useState<GraphQL.PublicOrderStatus[]>(() =>
    getInitialStatuses<GraphQL.PublicOrderStatus>(GraphQL.OrderSearchField.Status, initialFilters)
  );
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<GraphQL.PaymentStatus[]>(() =>
    getInitialStatuses<GraphQL.PaymentStatus>(GraphQL.OrderSearchField.PaymentStatus, initialFilters)
  );
  const [orderDate, setOrderDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.CreatedAt, initialFilters)
  );
  const [statusUpdatedDate, setStatusUpdatedDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.UpdatedAt, initialFilters)
  );
  const [printedDate, setPrintedDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.OrderSearchField.SubmittedAt, initialFilters)
  );

  const handleApply = (customerPublicId: string) => {
    const customerPublicIdFilter =
      customerPublicId !== NO_FILTER_VALUE
        ? {
            field: GraphQL.OrderSearchField.CustomerPublicId,
            condition: GraphQL.SearchCondition.Equals,
            firstValue: customerPublicId,
          }
        : null;

    const filters = toSearchInputArray([
      customerPublicIdFilter,
      getInStatusFilter(GraphQL.OrderSearchField.Status, orderStatusFilter),
      getInStatusFilter(GraphQL.OrderSearchField.PaymentStatus, paymentStatusFilter),
      getDateRangeFilter(GraphQL.OrderSearchField.CreatedAt, orderDate),
      getDateRangeFilter(GraphQL.OrderSearchField.UpdatedAt, statusUpdatedDate),
      getDateRangeFilter(GraphQL.OrderSearchField.SubmittedAt, printedDate),
    ]);

    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setOrderStatusFilter([]);
    setPaymentStatusFilter([]);
    setOrderDate([null, null]);
    setStatusUpdatedDate([null, null]);
    setPrintedDate([null, null]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        {organizations && (
          <RpaDropdown
            data-testid="organizations-dropdown"
            label="Organizations"
            data={toSelectionData(organizations)}
            searchable
            searchValue={orgSearch}
            onSearchChange={setOrgSearch}
            onDropdownOpen={() => setOrgSearch("")}
            {...getInputProps("selectedCustomer")}
          />
        )}
        <MultiselectInput
          label="Order Status"
          items={allOrderStatuses}
          value={orderStatusFilter.map((value) => ({ id: value, label: publicOrderStatusRender(value) }))}
          onValueChange={(newValue) => {
            setOrderStatusFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setOrderStatusFilter((currentStatuses) => {
              const filteredStatuses = currentStatuses.filter((status) => status !== valueToDelete.id);
              return filteredStatuses;
            });
          }}
        />
        <MultiselectInput
          label="Payment Status"
          items={allPaymentStatuses}
          value={paymentStatusFilter.map((value) => ({ id: value, label: paymentStatusRender(value) }))}
          onValueChange={(newValue) => {
            setPaymentStatusFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setPaymentStatusFilter((currentStatuses) => {
              const filteredStatuses = currentStatuses.filter((status) => status !== valueToDelete.id);
              return filteredStatuses;
            });
          }}
        />
        <DatePickerInput
          label="Order Date"
          range
          initialMonth={orderDate?.[0] || undefined}
          value={orderDate}
          onChange={setOrderDate}
        />
        <DatePickerInput
          label="Status Updated Date"
          range
          initialMonth={statusUpdatedDate?.[0] || undefined}
          value={statusUpdatedDate}
          onChange={setStatusUpdatedDate}
        />
        <DatePickerInput
          label="Printed Date"
          range
          initialMonth={printedDate?.[0] || undefined}
          value={printedDate}
          onChange={setPrintedDate}
        />
      </form>
    </Stack>
  );
}

const allOrderStatuses: MultiselectValue<GraphQL.PublicOrderStatus>[] = Object.values(GraphQL.PublicOrderStatus).map(
  (status) => ({ id: status, label: publicOrderStatusRender(status) })
);

const allPaymentStatuses: MultiselectValue<GraphQL.PaymentStatus>[] = Object.values(GraphQL.PaymentStatus).map(
  (status) => ({ id: status, label: paymentStatusRender(status) })
);

function toSelectionData(
  organizations?: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"]
): SelectItem[] {
  const customersAsSelectData =
    organizations?.map<SelectItem>((customer) => ({
      label: customer.organizationName ? `${customer.organizationName} (${customer.publicId})` : customer.publicId,
      value: customer.publicId,
    })) ?? [];
  const all: SelectItem = { label: "All", value: NO_FILTER_VALUE };
  return [all, ...customersAsSelectData];
}
